import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BackupIcon from '@mui/icons-material/Backup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import { TwoFactorSetup } from './TwoFactorSetup';
import { authAPI } from '../../services/api';

interface TwoFactorSettingsProps {
  user: {
    two_factor_enabled?: boolean;
    has_2fa_setup?: boolean;
    email: string;
  };
  onUpdate: () => void;
}

export const TwoFactorSettings: React.FC<TwoFactorSettingsProps> = ({
  user,
  onUpdate
}) => {
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [hasExistingSetup, setHasExistingSetup] = useState(false);

  const isEnabled = user.two_factor_enabled || false;
  // Use backend-provided has_2fa_setup or local state
  const hasSetup = user.has_2fa_setup || hasExistingSetup;

  // When component mounts, sync local state with backend data
  useEffect(() => {
    // If backend says there's a setup, update local state
    if (user.has_2fa_setup) {
      setHasExistingSetup(true);
    }
  }, [user.has_2fa_setup]);

  const handleToggle2FA = async () => {
    if (hasSetup) {
      // User has 2FA setup, just toggle enable/disable
      setLoading(true);
      try {
        if (isEnabled) {
          await authAPI.disable2FA();
        } else {
          await authAPI.enableExisting2FA();
        }
        onUpdate();
        setError(null);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Greška prilikom promene statusa 2FA');
      } finally {
        setLoading(false);
      }
    } else {
      // User doesn't have 2FA setup, show setup dialog
      setSetupDialogOpen(true);
    }
  };

  const handleSetupComplete = () => {
    setHasExistingSetup(true); // Mark that setup now exists
    onUpdate();
    setError(null);
  };

  const handleCompleteReset = async () => {
    setLoading(true);
    try {
      await authAPI.reset2FA();
      setHasExistingSetup(false); // Mark that setup no longer exists
      onUpdate();
      setError(null);
      setResetConfirmOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Neuspešno uklanjanje 2FA setup-a');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    setDownloading(true);
    try {
      // Pozovi API za generisanje novih kodova
      const response = await authAPI.regenerateBackupCodes();
      const codes = response.backup_codes || [];
      // Pripremi txt fajl
      const codesText = codes.join('\n');
      const blob = new Blob([
        `OmniPass Backup Kodovi\nGenerisano: ${new Date().toLocaleString()}\n\n${codesText}\n\nSačuvajte ove kodove na sigurnom mestu!`
      ], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'omnipass-backup-kodovi.txt';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      onUpdate();
    } catch (err) {
      setError('Neuspešno generisanje backup kodova.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <SecurityIcon color="primary" fontSize="large" />
          <Box flex={1}>
            <Typography variant="h6">
              Dvostruka Autentifikacija
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dodajte dodatni sloj bezbednosti vašem nalogu
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={isEnabled}
                onChange={handleToggle2FA}
                disabled={loading}
              />
            }
            label={isEnabled ? "Uključeno" : "Isključeno"}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {hasSetup && isEnabled ? (
          <Box>
            <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Dvostruka autentifikacija je aktivna na vašem nalogu. Vaš nalog je sigurniji!
              </Typography>
            </Alert>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <QrCode2Icon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Aplikacija za Autentifikaciju"
                  secondary="Google Authenticator ili slična aplikacija"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <BackupIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Rezervni Kodovi"
                  secondary="Za oporavak pristupa"
                />
                <ListItemSecondaryAction>
                  <Button size="small" variant="outlined" onClick={handleRegenerateBackupCodes} disabled={downloading}>
                    {downloading ? 'Generišem...' : 'Regeneriši kodove'}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            </List>

            <Box mt={2} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setResetConfirmOpen(true)}
                disabled={loading}
              >
                Ukloni 2FA Setup
              </Button>
            </Box>
          </Box>
        ) : hasSetup && !isEnabled ? (
          <Box>
            <Alert severity="info" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Dvostruka autentifikacija je podešena ali trenutno isključena. Možete je ponovo uključiti prekidačem iznad.
              </Typography>
            </Alert>

              <List dense sx={{ mb: 2 }}>
                <ListItem>
                  <ListItemIcon>
                    <QrCode2Icon color="disabled" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Aplikacija za Autentifikaciju"
                    secondary="Podešena, ali isključena"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <BackupIcon color="disabled" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Rezervni Kodovi"
                    secondary="Dostupni kada se 2FA uključi"
                  />
                </ListItem>
              </List>

              <Box mt={2}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setResetConfirmOpen(true)}
                  disabled={loading}
                >
                  Ukloni 2FA Setup
                </Button>
              </Box>
            </Box>
          ) : (
          <Box>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                Vaš nalog nije zaštićen dvostrukom autentifikacijom. Snažno preporučujemo da je uključite.
              </Typography>
            </Alert>

            <Typography variant="body2" sx={{ mb: 3 }}>
              Dvostruka autentifikacija pruža dodatni sloj bezbednosti tako što zahteva i vašu lozinku
              i kod sa mobilnog uređaja za prijavu.
            </Typography>

            <List dense sx={{ mb: 2 }}>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Štiti od neovlašćenog pristupa" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Radi sa popularnim aplikacijama za autentifikaciju" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Uključuje rezervne kodove za oporavak" />
              </ListItem>
            </List>

            <Button
              variant="contained"
              startIcon={<SecurityIcon />}
              onClick={handleToggle2FA}
              disabled={loading}
            >
              Podesi Dvostruku Autentifikaciju
            </Button>
          </Box>
        )}

        {/* Setup Dialog */}
        <TwoFactorSetup
          open={setupDialogOpen}
          onClose={() => setSetupDialogOpen(false)}
          onSetupComplete={handleSetupComplete}
        />

        {/* Reset Confirmation Dialog */}
        <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)}>
          <DialogTitle sx={{ color: 'error.main' }}>
            Uklanjanje 2FA Setup-a
          </DialogTitle>
          <DialogContent>
            <Typography>
              Da li ste sigurni da želite potpuno ukloniti 2FA setup? Ova akcija će:
            </Typography>
            <List dense sx={{ mt: 2 }}>
              <ListItem>
                <ListItemText primary="• Ukloniti tajni ključ za autentifikaciju" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Obrisati sve rezervne kodove" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• Zahtevati novo podešavanje 2FA ako je budete ponovo želeli" />
              </ListItem>
            </List>
            <Typography sx={{ mt: 2, fontWeight: 'bold', color: 'error.main' }}>
              Ova akcija se ne može poništiti!
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetConfirmOpen(false)}>
              Otkaži
            </Button>
            <Button
              onClick={handleCompleteReset}
              color="error"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Uklanjam...' : 'Ukloni 2FA'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};
